import React, { useState, useEffect } from 'react';
import { RiMapPinLine, RiAddLine, RiArrowDownSLine, RiArrowUpLine, RiCheckboxCircleLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import FlightTypeToggle from './FlightTypeToggle';
import FlightForm from './FlightForm';
import { isFlightDetailsComplete, validateFlightDetails } from '../../utils/flightValidation';
import { useAppStore } from '@store/appStore';
/**
 * FlightDetailsSection Component
 * Card-style section for flight details with type toggle and flight forms
 *
 * @param {Object} props
 * @param {Object} props.data - Section data { type: 'one-way'|'round-trip'|'multi-leg', flights: Array }
 * @param {Function} props.onUpdate - Callback when section updates
 * @param {boolean} props.isComplete - Whether section is complete
 * @param {boolean} props.isExpanded - Whether section is expanded
 * @param {Function} props.onToggle - Callback when section is toggled
 * @param {Function} props.onNext - Callback when Next button is clicked
 * @param {Function} props.onBack - Callback when Back button is clicked
 * @param {boolean} props.isClientComplete - Whether client section is complete (prerequisite)
 * @param {boolean} props.isUnlocked - Whether section has been unlocked by clicking Next
 * @param {boolean} props.disabled - Whether section is disabled
 */
const FlightDetailsSection = ({
  data = { type: 'one-way', flights: [{ departure: null, arrival: null, departureDate: '', returnDate: '', passengerCount: 1, notes: '' }] },
  onUpdate,
  isComplete = false,
  isExpanded = false,
  onToggle,
  onNext,
  onBack,
  isClientComplete = false,
  isUnlocked = false,
  disabled = false
}) => {

  const { selectedBid } = useAppStore.getState();
  
  const [sectionData, setSectionData] = useState({
    type: 'one-way',
    flights: [{
      departure: selectedBid?.departure_airport ? { code: selectedBid.departure_airport, name: selectedBid.departure_airport } : null,
      arrival: selectedBid?.arrival_airport ? { code: selectedBid.arrival_airport, name: selectedBid.arrival_airport } : null,
      departureDate: selectedBid?.departure_date ? new Date(selectedBid.departure_date) : '',
      returnDate: '',
      passengerCount: selectedBid?.passenger_count || 1,
      notes: ''
    }],
    ...data
  });

  const [validationErrors, setValidationErrors] = useState([]);
  const [isFlightValid, setIsFlightValid] = useState(false);

  // Update state when data prop changes
  useEffect(() => {
    setSectionData(prev => ({
      ...prev,
      ...data
    }));
  }, [data]);

  // Validate on data change
  useEffect(() => {
    const validation = validateFlightDetails(sectionData.flights, sectionData.type);
    setValidationErrors(validation.errors);
    setIsFlightValid(validation.isValid);
  }, [sectionData]);

  // Handle flight type change
  const handleTypeChange = (newType) => {
    if (disabled || !isClientComplete) return;
    if (newType === sectionData.type) return;

    let updatedFlights = [...sectionData.flights];

    // Handle type-specific adjustments
    if (newType === 'one-way') {
      // One-way: keep only first flight, remove return date
      updatedFlights = [{
        ...updatedFlights[0],
        returnDate: ''
      }];
    } else if (newType === 'round-trip') {
      // Round-trip: 2 flights (outbound + return with swapped airports)
      const first = updatedFlights[0];
      updatedFlights = [
        first,
        {
          ...first,
          departure: first.arrival,
          arrival: first.departure,
          departureDate: first.returnDate || '',
          departureTime: first.returnTime || '',
          returnDate: '',
          returnTime: '',
        },
      ];
    } else if (newType === 'multi-leg') {
      // Multi-leg: ensure at least 2 flights, remove return dates.
      // SPECIAL CASE — converting from round-trip with the 1-leg+returnDate
      // form shape would otherwise silently lose the return-trip data. Split
      // the round-trip into two explicit legs so nothing disappears.
      const wasRoundTripOneLeg =
        sectionData.type === 'round-trip' &&
        updatedFlights.length === 1 &&
        (updatedFlights[0]?.returnDate || updatedFlights[0]?.returnTime);
      if (wasRoundTripOneLeg) {
        const out = updatedFlights[0];
        updatedFlights = [
          { ...out, returnDate: '', returnTime: '' },
          {
            ...out,
            departure: out.arrival,
            arrival: out.departure,
            departureDate: out.returnDate,
            departureTime: out.returnTime,
            returnDate: '',
            returnTime: '',
          },
        ];
      } else {
        if (updatedFlights.length < 2) {
          updatedFlights.push({
            departure: null,
            arrival: null,
            departureDate: '',
            returnDate: '',
            passengerCount: 1,
            notes: ''
          });
        }
        // Remove return dates from all flights (multi-leg doesn't use them).
        updatedFlights = updatedFlights.map(flight => ({
          ...flight,
          returnDate: '',
          returnTime: '',
        }));
      }
    }

    const updatedData = {
      type: newType,
      flights: updatedFlights
    };

    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Handle flight form change
  const handleFlightChange = (index, flightData) => {
    const updatedFlights = [...sectionData.flights];
    const prevFlight = updatedFlights[index];
    updatedFlights[index] = flightData;

    // Round-trip: sync return leg airports when first leg departure/arrival changes
    if (sectionData.type === 'round-trip' && index === 0 && updatedFlights.length === 2) {
      const depChanged = prevFlight.departure !== flightData.departure;
      const arrChanged = prevFlight.arrival !== flightData.arrival;
      if (depChanged || arrChanged) {
        updatedFlights[1] = {
          ...updatedFlights[1],
          departure: flightData.arrival,
          arrival: flightData.departure,
        };
      }
    }

    const updatedData = {
      ...sectionData,
      flights: updatedFlights
    };

    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Handle add flight (multi-leg)
  const handleAddFlight = () => {
    if (disabled || !isClientComplete) return;

    const updatedFlights = [
      ...sectionData.flights,
      {
        departure: null,
        arrival: null,
        departureDate: '',
        returnDate: '',
        passengerCount: 1,
        notes: ''
      }
    ];

    const updatedData = {
      ...sectionData,
      flights: updatedFlights
    };

    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Handle remove flight (multi-leg)
  const handleRemoveFlight = (index) => {
    if (disabled || !isClientComplete || sectionData.flights.length <= 2) return;

    const updatedFlights = sectionData.flights.filter((_, i) => i !== index);

    const updatedData = {
      ...sectionData,
      flights: updatedFlights
    };

    setSectionData(updatedData);
    onUpdate?.(updatedData);
  };

  // Determine if showing return date
  const showReturnDate = sectionData.type === 'round-trip';

  // Determine if multi-leg
  const isMultiLeg = sectionData.type === 'multi-leg';

  // Generate summary text for collapsed state
  const getSummaryText = () => {
    if (!sectionData.flights || sectionData.flights.length === 0) {
      return 'No flights added';
    }

    const firstFlight = sectionData.flights[0];
    const departure = firstFlight.departure?.icao || '—';
    const arrival = firstFlight.arrival?.icao || '—';

    if (sectionData.type === 'round-trip') {
      return `Round Trip: ${departure} ↔ ${arrival}`;
    } else if (sectionData.type === 'multi-leg') {
      return `Multi-Leg: ${sectionData.flights.length} flights from ${departure}`;
    } else {
      return `${departure} → ${arrival}`;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
      {/* Section Header - Always Visible */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled || !isUnlocked}
        className={cn(
          'w-full flex items-center justify-between p-6 text-left transition-colors',
          (disabled || !isUnlocked) && 'cursor-not-allowed opacity-60'
        )}
      >
        <div className="flex items-center gap-3 flex-1">
          <RiMapPinLine className="w-5 h-5 text-gray-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Flight Details</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isComplete && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full">
              <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
            </div>
          )}
        </div>
      </button>

      {/* Section Content - Shown when expanded */}
      {isExpanded && (
        <div className="px-6 pb-6">
          {/* Flight Type Toggle */}
          <div className="mb-6">
            <FlightTypeToggle
              activeType={sectionData.type}
              onChange={handleTypeChange}
              disabled={disabled || !isClientComplete}
            />
          </div>

          {/* Flight Forms */}
          <div className="space-y-4">
            {sectionData.flights.map((flight, index) => (
              <div key={index}>
                <FlightForm
                  data={flight}
                  onChange={(flightData) => handleFlightChange(index, flightData)}
                  errors={validationErrors[index] || {}}
                  showReturnDate={false}
                  legNumber={isMultiLeg ? index + 1 : null}
                  onRemove={isMultiLeg ? () => handleRemoveFlight(index) : null}
                  isRemovable={isMultiLeg && sectionData.flights.length > 2}
                  disabled={disabled || !isClientComplete}
                  selectedBid={selectedBid}
                />
              </div>
            ))}
          </div>

          {/* Add Flight Button (Multi-Leg) */}
          {isMultiLeg && (
            <button
              type="button"
              onClick={handleAddFlight}
              disabled={disabled || !isClientComplete}
              className={cn(
                'mt-4 w-full flex items-center bg-white justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all',
                'border-2 border-dashed',
                'focus:outline-none focus:ring-2 focus:ring-blue-500',
                (disabled || !isClientComplete)
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'border-primary-500 text-primary-700 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50'
              )}
            >
              <RiAddLine className="w-5 h-5" />
              Add Flight
            </button>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onBack}
              disabled={disabled}
              className={cn(
                'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'flex items-center justify-center gap-2',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <RiArrowUpLine className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!isClientComplete || !isFlightValid}
              className={cn(
                'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'bg-accent text-primary-500 hover:bg-accent',
                'focus:outline-none focus:ring-0 focus:ring-blue-0 focus:ring-offset-0',
                'flex items-center justify-center gap-2',
                (!isClientComplete || !isFlightValid) && 'opacity-50 cursor-not-allowed'
              )}
            >
              Next
              <RiArrowDownSLine className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightDetailsSection;
